import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const LineChart = ({ data, darkMode }) => {
  if (!data) return null;

  const isOldFormat = Array.isArray(data['chart-data']);
  const isNewFormat = Array.isArray(data.series);

  if (!isOldFormat && !isNewFormat) return null;

  // Dark mode theme configuration
  const chartTheme = darkMode
    ? {
        backgroundColor: '#234046',
        textColor: '#FFFFFF',
        gridLineColor: '#3B3B3B',
        lineColor: '#575757',
      }
    : {
        backgroundColor: '#FFFFFF',
        textColor: '#2B2B2B',
        gridLineColor: '#E6E6E6',
        lineColor: '#CCCCCC',
      };

  let categories = [];
  let series = [];

  /* ---------------- OLD FORMAT (ENHANCED, NOT CHANGED) ---------------- */
  if (isOldFormat) {
    categories = data['chart-data'].map((d) => {
      // ✅ ADDITION: PM-KISAN friendly label
      if (d.period && d.installment) {
        return `${d.installment} Inst.\n${d.period}`;
      }

      // ✅ Existing behavior
      return d.year || d.period;
    });

    const seriesConfig = data.seriesConfig || [
      {
        key: 'value',
        name: data.label || '',
        color: '#3B82F6',
        yAxis: 0,
      },
    ];

    series = seriesConfig.map((cfg) => ({
      name: cfg.name,
      color: cfg.color,
      yAxis: cfg.yAxis || 0,
      data: data['chart-data'].map((d) => d[cfg.key]),
      marker: {
        enabled: true,
        radius: 4,
      },
    }));
  }

  /* ---------------- NEW FORMAT (NO CHANGE) ---------------- */
  if (isNewFormat) {
    categories = data.series[0]?.data?.map((d) => d.x) || [];

    series = data.series.map((s) => ({
      name: s.name,
      color: s.color,
      data: s.data.map((d) => d.y),
      marker: {
        enabled: true,
        radius: 4,
      },
    }));
  }

  /* ---------------- Y AXIS (NO CHANGE) ---------------- */
  const yAxis = Array.isArray(data.yAxis)
    ? data.yAxis.map((axis) => ({
        title: {
          text: axis.title,
          style: {
            color: chartTheme.textColor,
          },
        },
        opposite: axis.opposite || false,
        min: axis.min,
        max: axis.max,
        labels: {
          style: {
            color: chartTheme.textColor,
          },
        },
        gridLineColor: chartTheme.gridLineColor,
      }))
    : [
        {
          title: {
            text: data?.yAxis?.title || null,
            style: {
              color: chartTheme.textColor,
            },
          },
          min: data?.yAxis?.min,
          max: data?.yAxis?.max,
          opposite: false,
          labels: {
            style: {
              color: chartTheme.textColor,
            },
          },
          gridLineColor: chartTheme.gridLineColor,
        },
      ];

  const options = {
    chart: {
      type: 'line',
      height: 420,
      backgroundColor: chartTheme.backgroundColor,
    },

    title: {
      text: data.title || null,
      style: {
        color: chartTheme.textColor,
      },
    },

    xAxis: {
      categories,
      title: {
        text: data?.xAxis?.title,
        style: {
          color: chartTheme.textColor,
        },
      },
      labels: {
        style: {
          fontSize: '11px',
          color: chartTheme.textColor,
        },
      },
      lineColor: chartTheme.lineColor,
      tickColor: chartTheme.lineColor,
    },

    yAxis,

    tooltip: {
      shared: true,
      valueSuffix: data?.tooltip?.valueSuffix || '',
      backgroundColor: darkMode ? '#3B3B3B' : '#FFFFFF',
      borderColor: darkMode ? '#575757' : '#CCCCCC',
      style: {
        color: chartTheme.textColor,
      },
    },

    legend: {
      enabled: data?.legend !== false,
      itemStyle: {
        color: chartTheme.textColor,
      },
      itemHoverStyle: {
        color: darkMode ? '#FFFFFF' : '#000000',
      },
    },

    series,

    /* ✅ RESPONSIVE ADDITION (SAFE) */
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 768,
          },
          chartOptions: {
            xAxis: {
              labels: {
                rotation: -45,
                style: {
                  fontSize: '10px',
                  color: chartTheme.textColor,
                },
              },
            },
          },
        },
      ],
    },

    credits: {
      enabled: false,
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default LineChart;
